import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SearchDialog from "@/components/SearchDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LayoutDashboard, Briefcase, Users, PackageCheck, BarChart3, UserCog,
  Settings, LogOut, Plus, Search, Menu, Activity
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/ready", label: "Ready for Delivery", icon: PackageCheck },
  { to: "/reports", label: "Reports", icon: BarChart3, admin: true },
  { to: "/activity", label: "Activity Log", icon: Activity, admin: true },
  { to: "/staff", label: "Staff Management", icon: UserCog, admin: true },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const items = NAV_ITEMS.filter((i) => !i.admin || user?.role === "admin");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = ({ onClick }) => (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
        >
          <item.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
          {item.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-amber-900/10 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 h-14">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-4">
              <div className="flex items-center mb-6 px-2">
                <img src="/reshine-logo.png" alt="Reshine India" className="h-10 w-auto" />
              </div>
              <nav className="flex flex-col gap-1">
                <NavItems onClick={() => setMenuOpen(false)} />
                <button data-testid="nav-logout-link" onClick={handleLogout}
                  className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                  <LogOut className="h-[18px] w-[18px]" /> Logout
                </button>
              </nav>
            </SheetContent>
          </Sheet>
          <NavLink to="/" className="flex items-center" data-testid="brand-logo-link">
            <img src="/reshine-logo.png" alt="Reshine India" className="h-9 w-auto" />
          </NavLink>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="hidden sm:flex gap-2 text-muted-foreground" onClick={() => setSearchOpen(true)} data-testid="global-search-open-button">
            <Search className="h-4 w-4" /> Search…
          </Button>
          <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => setSearchOpen(true)} data-testid="global-search-open-button-mobile">
            <Search className="h-5 w-5" />
          </Button>
          {user?.role !== "operator" && (
            <Button size="sm" className="hidden md:inline-flex font-semibold" onClick={() => navigate("/new-job")} data-testid="header-new-job-button">
              <Plus className="h-4 w-4 mr-1" /> New Job
            </Button>
          )}
          <div className="hidden lg:flex items-center gap-2 pl-2" data-testid="header-user-info">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">
                {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="leading-tight">
              <p className="text-xs font-semibold">{user?.name}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{user?.role}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 shrink-0 border-r min-h-[calc(100vh-3.5rem)] p-4 sticky top-14 self-start">
          <nav className="flex flex-col gap-1">
            <NavItems />
            <button data-testid="nav-logout-desktop" onClick={handleLogout}
              className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors mt-4">
              <LogOut className="h-[18px] w-[18px]" /> Logout
            </button>
          </nav>
        </aside>

        <main className="flex-1 min-w-0 px-4 py-5 sm:px-6 pb-28 lg:pb-10">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5 h-16 items-center">
          <NavLink to="/" end data-testid="bottomnav-dashboard" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <LayoutDashboard className="h-5 w-5" />Home
          </NavLink>
          <NavLink to="/jobs" data-testid="bottomnav-jobs" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <Briefcase className="h-5 w-5" />Jobs
          </NavLink>
          <div className="flex justify-center">
            {user?.role === "operator" ? (
              <button onClick={() => setSearchOpen(true)} data-testid="fab-search"
                className="h-14 w-14 -mt-8 rounded-full bg-secondary text-secondary-foreground shadow-lg shadow-amber-900/20 flex items-center justify-center active:scale-95 transition-transform border-4 border-background">
                <Search className="h-6 w-6" />
              </button>
            ) : (
              <button onClick={() => navigate("/new-job")} data-testid="fab-new-job"
                className="h-14 w-14 -mt-8 rounded-full bg-primary text-primary-foreground shadow-lg shadow-amber-900/30 flex items-center justify-center active:scale-95 transition-transform border-4 border-background">
                <Plus className="h-7 w-7" />
              </button>
            )}
          </div>
          <NavLink to="/customers" data-testid="bottomnav-customers" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <Users className="h-5 w-5" />Customers
          </NavLink>
          <NavLink to="/ready" data-testid="bottomnav-ready" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <PackageCheck className="h-5 w-5" />Ready
          </NavLink>
        </div>
      </nav>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
